import { ButtonGraphicsDecorationType } from '../Model/StyleModel.js';
import { assertNever } from '../Util.js';
import { ButtonDecorationRenderer } from './ButtonDecorationRenderer.js';
import { DrawBounds, parseColor, rgbRev } from './Util.js';
export class GraphicsLayeredButtonRenderer {
    static async draw(img, drawStyle, elementsToHide, selectedElementId, paddingPx) {
        const backgroundElement = drawStyle.elements[0]?.type === 'canvas' ? drawStyle.elements[0] : undefined;
        const drawWidth = img.width - paddingPx.x * 2;
        const drawHeight = img.height - paddingPx.y * 2;
        let decoration = backgroundElement?.decoration;
        if (decoration === ButtonGraphicsDecorationType.FollowDefault || decoration === undefined) {
            decoration = drawStyle.show_topbar ? ButtonGraphicsDecorationType.TopBar : ButtonGraphicsDecorationType.Border;
        }
        const showTopBar = decoration === ButtonGraphicsDecorationType.TopBar;
        const topBarBounds = new DrawBounds(paddingPx.x, paddingPx.y, drawWidth, Math.max(ButtonDecorationRenderer.DEFAULT_HEIGHT, Math.floor(0.2 * drawHeight)));
        const topBarHeight = showTopBar ? topBarBounds.height : 0;
        const drawBounds = new DrawBounds(paddingPx.x, paddingPx.y + topBarHeight, drawWidth, drawHeight - topBarHeight);
        this.#drawBackgroundElement(img, drawBounds, backgroundElement);
        const selectedElementBounds = await this.#drawElements(img, drawStyle.elements, elementsToHide, selectedElementId, drawBounds, drawBounds, false);
        switch (decoration) {
            case ButtonGraphicsDecorationType.None:
                // Do nothing
                break;
            case ButtonGraphicsDecorationType.Border:
                ButtonDecorationRenderer.drawBorderWhenPushed(img, drawStyle, drawBounds);
                break;
            case ButtonGraphicsDecorationType.TopBar:
                ButtonDecorationRenderer.drawStatusBar(img, drawStyle, topBarBounds, false);
                break;
            default:
                assertNever(decoration);
                break;
        }
        // Draw top status icons
        if (drawStyle.show_status_icons) {
            ButtonDecorationRenderer.drawIcons(img, drawStyle, topBarBounds);
        }
        // Draw a border around the selected element, do this last so it's on top
        if (selectedElementBounds)
            this.#drawBoundsLines(img, selectedElementBounds);
    }
    /**
     * Draw the elements to the image
     * Returns the selected element bounds, or null if no element was selected or the selected element was not found
     */
    static async #drawElements(img, elements, elementsToHide, selectedElementId, rootBounds, drawBounds, skipDrawParent) {
        let selectedElementBounds = null;
        for (const element of elements) {
            // Skip the background element, it's handled separately
            if (element.type === 'canvas')
                continue;
            const skipDraw = skipDrawParent || elementsToHide.has(element.id) || !element.enabled;
            let elementBounds = null;
            try {
                switch (element.type) {
                    case 'group': {
                        await img.usingTemporaryLayer(element.opacity, async (img) => {
                            await img.usingRotation(drawBounds, element.rotation, async () => {
                                elementBounds = await this.#drawGroupElement(img, drawBounds, element, skipDraw);
                                // Propagate the selected
                                const childElementBounds = await this.#drawElements(img, element.children, elementsToHide, selectedElementId, rootBounds, elementBounds, skipDraw);
                                if (childElementBounds)
                                    selectedElementBounds = childElementBounds;
                            });
                        });
                        break;
                    }
                    case 'image':
                        elementBounds = await this.#drawImageElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'text':
                        elementBounds = await this.#drawTextElement(img, rootBounds, drawBounds, element, skipDraw);
                        break;
                    case 'box':
                        elementBounds = await this.#drawBoxElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'line':
                        elementBounds = await this.#drawLineElement(img, drawBounds, element, skipDraw);
                        break;
                    case 'circle':
                        elementBounds = await this.#drawCircleElement(img, drawBounds, element, skipDraw);
                        break;
                    default:
                        assertNever(element);
                }
            }
            catch (_e) {
                // TODO - log/report error where? Or should this abandon the render and do a placeholder?
            }
            // Find the bounds of the selected element
            if (element.id === selectedElementId)
                selectedElementBounds = elementBounds;
        }
        return selectedElementBounds;
    }
    static #drawBackgroundElement(_img, _drawBounds, backgroundElement) {
        if (!backgroundElement)
            return;
        // img.box(drawBounds.x, drawBounds.y, drawBounds.maxX, drawBounds.maxY, parseColor(backgroundElement.color))
    }
    static async #drawGroupElement(_img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw)
            return drawBounds;
        return drawBounds;
    }
    static async #drawImageElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw || !element.base64Image)
            return drawBounds;
        try {
            const imageData = element.base64Image;
            await img.usingAlpha(element.opacity, async () => {
                await img.usingRotation(drawBounds, element.rotation, async () => {
                    await img.drawBase64Image(imageData, drawBounds.x, drawBounds.y, drawBounds.width, drawBounds.height, element.halign, element.valign, element.fillMode);
                });
            });
        }
        catch (e) {
            console.error('error drawing image:', e);
            await img.usingRotation(drawBounds, element.rotation, async () => {
                await img.usingTemporaryLayer(element.opacity, async (img) => {
                    // Draw a thick red cross
                    img.drawPath([
                        [drawBounds.x, drawBounds.y],
                        [drawBounds.maxX, drawBounds.maxY],
                    ], { color: 'red', width: 5 });
                    img.drawPath([
                        [drawBounds.x, drawBounds.maxY],
                        [drawBounds.maxX, drawBounds.y],
                    ], { color: 'red', width: 5 });
                });
            });
        }
        return drawBounds;
    }
    static async #drawTextElement(img, rootBounds, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw || !element.text)
            return drawBounds;
        // Draw button text
        let fontSize = Number(element.fontsize) || 'auto';
        // Force some padding around the text, scaled proportionally
        const marginScale = 0.015;
        const marginX = 2 * marginScale;
        const marginY = 1 * marginScale;
        if (typeof fontSize === 'number') {
            // Scale font to be a percentage relative to the height of the usable button space
            // Future: should this be relative to the bounds of the text element?
            fontSize *= rootBounds.height / 100 / 1.2;
        }
        await img.usingAlpha(element.opacity, async () => {
            await img.usingRotation(drawBounds, element.rotation, async () => {
                img.drawAlignedText(drawBounds.x + marginX, drawBounds.y + marginY, drawBounds.width - 2 * marginX, drawBounds.height - 2 * marginY, element.text, parseColor(element.color), fontSize, element.halign, element.valign, rgbRev(element.outlineColor, true).a > 0
                    ? {
                        width: 2, // Fixed width for now, maybe should be dynamic
                        color: parseColor(element.outlineColor),
                    }
                    : undefined);
            });
        });
        return drawBounds;
    }
    static async #drawBoxElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw)
            return drawBounds;
        // Calculate a pixel width, relative to the parent bounds
        const borderWidth = Math.max(0, parentBounds.width, parentBounds.height) * element.borderWidth;
        await img.usingAlpha(element.opacity, async () => {
            await img.usingRotation(drawBounds, element.rotation, async () => {
                img.box(drawBounds.x, drawBounds.y, drawBounds.maxX, drawBounds.maxY, parseColor(element.color), {
                    color: parseColor(element.borderColor),
                    width: borderWidth,
                }, element.borderPosition);
            });
        });
        return drawBounds;
    }
    static async #drawLineElement(img, parentBounds, element, skipDraw) {
        // Convert from fractional coordinates (0-1) to pixel coordinates within parent bounds
        const fromX = parentBounds.x + element.fromX * parentBounds.width;
        const fromY = parentBounds.y + element.fromY * parentBounds.height;
        const toX = parentBounds.x + element.toX * parentBounds.width;
        const toY = parentBounds.y + element.toY * parentBounds.height;
        // Calculate bounds for selection (use the bounding box of the line)
        const minX = Math.min(fromX, toX);
        const minY = Math.min(fromY, toY);
        const maxX = Math.max(fromX, toX);
        const maxY = Math.max(fromY, toY);
        const drawBounds = new DrawBounds(minX, minY, maxX - minX, maxY - minY);
        if (skipDraw)
            return drawBounds;
        // Calculate a pixel width, relative to the parent bounds
        const borderWidth = Math.max(1, Math.max(parentBounds.width, parentBounds.height) * element.borderWidth);
        await img.usingAlpha(element.opacity, async () => {
            img.line(fromX, fromY, toX, toY, {
                color: parseColor(element.borderColor),
                width: borderWidth,
            });
        });
        return drawBounds;
    }
    static async #drawCircleElement(img, parentBounds, element, skipDraw) {
        const drawBounds = parentBounds.compose(element.x, element.y, element.width, element.height);
        if (skipDraw)
            return drawBounds;
        // Calculate a pixel width, relative to the parent bounds
        const borderWidth = Math.max(0, parentBounds.width, parentBounds.height) * element.borderWidth;
        await img.usingAlpha(element.opacity, async () => {
            const midX = drawBounds.x + drawBounds.width / 2;
            const midY = drawBounds.y + drawBounds.height / 2;
            const radiusX = drawBounds.width / 2;
            const radiusY = drawBounds.height / 2;
            const startAngle = this.#angleToRadians(element.startAngle + 90);
            const endAngle = this.#angleToRadians(element.endAngle + 90);
            img.circle(midX, midY, radiusX, radiusY, startAngle, endAngle, element.drawSlice, parseColor(element.color), {
                color: parseColor(element.borderColor),
                width: borderWidth,
            }, element.borderOnlyArc, element.borderPosition);
        });
        return drawBounds;
    }
    /**
     * Draw some bounds lines over the whole image, to give a visual indicator of the selected element
     * Note: this intentionally overshoots everything to make it very visible
     */
    static #drawBoundsLines(img, bounds) {
        const lineStyle = { color: 'rgb(255, 0, 0)', width: 1 }; // TODO - what colour is best?
        img.horizontalLine(bounds.y, lineStyle);
        img.horizontalLine(bounds.maxY, lineStyle);
        img.verticalLine(bounds.x, lineStyle);
        img.verticalLine(bounds.maxX, lineStyle);
    }
    static #angleToRadians(angle) {
        const normalizedAngle = (angle + 180) % 360;
        return (normalizedAngle / 180) * Math.PI;
    }
}
//# sourceMappingURL=LayeredRenderer.js.map